import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import AuthModal from "../../components/auth/AuthModal";
import GatewayTopNav from "../../components/dashboard/GatewayTopNav";
import GatewaySidebar from "../../components/dashboard/GatewaySidebar";
import SupportAiAssistantPanel from "../../components/dashboard/SupportAiAssistantPanel";
import { NOINDEX_PRIVATE_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import {
  SUPPORT_DASHBOARD_HERO_IMAGE,
  SUPPORT_FAST_HELP,
  SUPPORT_PRIORITIES,
  SUPPORT_TICKET_CATEGORIES,
  TREYDORA_DISCORD_INVITE_URL,
} from "../../data/supportDashboardMockData";
import {
  appendSupportTicketMessage,
  createSupportTicket,
  fetchMySupportTicket,
  fetchMySupportTickets,
  fetchMySupportTicketCounts,
} from "../../services/supportApi";
import "./treydora-gateway-dashboard.css";
import "./quests-dashboard.css";
import "./support-command-center.css";

const STATUS_BADGE_KEY = {
  Open: "open",
  "In Review": "review",
  "Waiting on User": "waiting",
  Resolved: "resolved",
  Closed: "closed",
};

function badgeClassForStatus(statusKey) {
  switch (statusKey) {
    case "open":
      return "dsc-badge dsc-badge--open";
    case "review":
      return "dsc-badge dsc-badge--review";
    case "waiting":
      return "dsc-badge dsc-badge--waiting";
    case "resolved":
      return "dsc-badge dsc-badge--resolved";
    case "closed":
      return "dsc-badge dsc-badge--closed";
    default:
      return "dsc-badge dsc-badge--open";
  }
}

function badgeClassForApiStatus(status) {
  return badgeClassForStatus(STATUS_BADGE_KEY[status] || "open");
}

function formatTicketDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

const MAX_ATTACH = 3;
const ACCEPT_ATTACH =
  "image/png,image/jpeg,image/webp,application/pdf,.png,.jpg,.jpeg,.webp,.pdf";

function mergeAttachmentFiles(existing, incoming) {
  const cur = Array.isArray(existing) ? [...existing] : [];
  const add = Array.isArray(incoming) ? incoming : [];
  for (const f of add) {
    if (!f || cur.length >= MAX_ATTACH) break;
    cur.push(f);
  }
  return cur.slice(0, MAX_ATTACH);
}

function SupportMessageAttachments({ items }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  return (
    <ul className="dsc-attachList">
      {list.map((a) => {
        const isImg = String(a.mimeType || "").startsWith("image/");
        const label = a.originalName || a.fileName || "Attachment";
        return (
          <li key={`${a.url}-${a.fileName}`} className="dsc-attachList__item">
            {isImg ? (
              <a href={a.url} target="_blank" rel="noopener noreferrer" className="dsc-attachList__thumbLink">
                <img src={a.url} alt="" className="dsc-attachList__thumb" />
                <span className="dsc-attachList__cap">{label}</span>
              </a>
            ) : (
              <a href={a.url} target="_blank" rel="noopener noreferrer" className="dsc-attachList__fileLink">
                <i className="fas fa-file-pdf dsc-attachList__icon" aria-hidden />
                {label}
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function SupportCommandCenterPage() {
  const { loading, isAuthenticated } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  const [category, setCategory] = useState(SUPPORT_TICKET_CATEGORIES[0]);
  const [priority, setPriority] = useState(SUPPORT_PRIORITIES[0]);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [reference, setReference] = useState("");

  const [createAttachmentFiles, setCreateAttachmentFiles] = useState([]);

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState("");
  const [supportCounts, setSupportCounts] = useState(null);

  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [replyBody, setReplyBody] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyError, setReplyError] = useState("");
  const [replySuccess, setReplySuccess] = useState("");
  const [replyAttachmentFiles, setReplyAttachmentFiles] = useState([]);

  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const loadSupportCounts = useCallback(async () => {
    try {
      const c = await fetchMySupportTicketCounts();
      setSupportCounts(c && typeof c === "object" ? c : null);
    } catch {
      setSupportCounts(null);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setTicketsLoading(true);
    setTicketsError("");
    try {
      const rows = await fetchMySupportTickets();
      setTickets(rows);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("treydora-support-tickets-updated"));
      }
    } catch (e) {
      setTickets([]);
      setTicketsError("Support tickets could not be loaded. Try again.");
    } finally {
      setTicketsLoading(false);
      void loadSupportCounts();
    }
  }, [loadSupportCounts]);

  usePageSeo({
    ...NOINDEX_PRIVATE_SEO,
    title: "Support Command Center | Treydora",
    description: "Official support channel for account, purchase, wallet, NFT, library, and storefront requests.",
  });

  useEffect(() => {
    if (isAuthenticated) {
      loadTickets();
    }
  }, [isAuthenticated, loadTickets]);

  useEffect(() => {
    if (!selectedTicketId) {
      setDetail(null);
      setDetailError("");
      setReplyBody("");
      setReplyError("");
      setReplySuccess("");
      setReplyAttachmentFiles([]);
      return undefined;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError("");
    fetchMySupportTicket(selectedTicketId)
      .then((d) => {
        if (!cancelled) {
          setDetail(d);
          setTickets((prev) =>
            prev.map((row) => (row.id === selectedTicketId ? { ...row, unreadForUser: false } : row))
          );
          void loadSupportCounts();
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("treydora-support-tickets-updated"));
          }
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setDetail(null);
          setDetailError(e?.message || "Could not load ticket details.");
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTicketId, loadSupportCounts]);

  const canSubmit = useMemo(() => {
    const s = subject.trim();
    const d = description.trim();
    return s.length >= 3 && d.length >= 10 && s.length <= 160 && d.length <= 5000;
  }, [subject, description]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!canSubmit || formBusy) return;
    setFormBusy(true);
    setFormError("");
    setFormSuccess("");
    try {
      const created = await createSupportTicket(
        {
          category,
          priority,
          subject: subject.trim(),
          description: description.trim(),
          relatedReference: reference.trim() || undefined,
        },
        createAttachmentFiles
      );
      setFormSuccess(
        `Ticket ${created?.ticketCode || ""} created. The Treydora team will review it in TreydoraED when routing is enabled.`
      );
      setSubject("");
      setDescription("");
      setReference("");
      setCreateAttachmentFiles([]);
      await loadTickets();
      if (created?.id) {
        setSelectedTicketId(created.id);
      }
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("treydora-support-tickets-updated"));
      }
    } catch (err) {
      setFormError(err?.message || "Could not create ticket.");
    } finally {
      setFormBusy(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!selectedTicketId || !detail || replyBusy) return;
    const body = replyBody.trim();
    if (body.length < 2 && replyAttachmentFiles.length === 0) {
      setReplyError("Add a message or attach at least one file (PNG, JPG, WebP, or PDF).");
      return;
    }
    if (body.length > 5000) {
      setReplyError("Message is too long (maximum 5000 characters).");
      return;
    }
    setReplyBusy(true);
    setReplyError("");
    setReplySuccess("");
    try {
      const updated = await appendSupportTicketMessage(
        selectedTicketId,
        body.length >= 2 ? body : "[Attachment update]",
        replyAttachmentFiles
      );
      setDetail(updated);
      setReplyBody("");
      setReplyAttachmentFiles([]);
      setReplySuccess("Your follow-up was sent.");
      await loadTickets();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("treydora-support-tickets-updated"));
      }
    } catch (err) {
      setReplyError(err?.message || "Could not add message.");
    } finally {
      setReplyBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-shell dashboard-shell--loading">
        <p className="dg-hud">Opening support channel…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="dashboard-shell dashboard-shell--gateway">
        <div className="dashboard-gate dg-gold-ring">
          <h1 className="dashboard-gate__title serif">Treydora Support</h1>
          <p className="dashboard-gate__lead">
            Sign in to open the Support Command Center, submit tickets to TreydoraEdBackend, and track your requests.
          </p>
          <button type="button" className="dg-btn dg-btn--primary" onClick={() => setAuthOpen(true)}>
            Sign In
          </button>
        </div>
        <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      </div>
    );
  }

  const unreadCount =
    supportCounts && typeof supportCounts.unreadForUser === "number" && !Number.isNaN(supportCounts.unreadForUser)
      ? supportCounts.unreadForUser
      : supportCounts && typeof supportCounts.unread === "number" && !Number.isNaN(supportCounts.unread)
        ? supportCounts.unread
        : 0;
  const needsUserCount =
    supportCounts &&
    typeof supportCounts.needsUserResponse === "number" &&
    !Number.isNaN(supportCounts.needsUserResponse)
      ? supportCounts.needsUserResponse
      : 0;
  const showUnreadAlert = unreadCount > 0;
  const showNeedsUserAlert = needsUserCount > 0;

  const replyLocked = detail?.status === "Closed";

  return (
    <div className="dashboard-shell dashboard-shell--gateway">
      <GatewayTopNav />
      <div className="dashboard-body dg-gateway-atmosphere">
        <GatewaySidebar />
        <div className="dashboard-workpad">
          <main className="qd quests-dash dsc-support" aria-label="Treydora Support Command Center">
            <div className="qd-layout">
              <div className="qd-main dsc-stack">
                <section
                  className="questsHero"
                  style={{ "--quests-hero-image": `url("${SUPPORT_DASHBOARD_HERO_IMAGE}")` }}
                  aria-labelledby="dsc-hero-title"
                >
                  <div className="questsHero__overlay" aria-hidden />
                  <div className="questsHero__content">
                    <span className="dsc-support__eyebrow">Official Support Channel</span>
                    <h1 id="dsc-hero-title" className="questsHero__title serif">
                      Treydora Support
                    </h1>
                    <p className="questsHero__intro">
                      Open account, purchase, wallet, NFT, library, and storefront requests without leaving your
                      dashboard.
                    </p>
                    <div className="dsc-routeStrip" role="status" aria-label="Support ticket routing">
                      <span>Treydora.com user ticket</span>
                      <span className="dsc-routeStrip__sep" aria-hidden>
                        →
                      </span>
                      <span>TreydoraEdBackend</span>
                      <span className="dsc-routeStrip__sep" aria-hidden>
                        →
                      </span>
                      <span>TreydoraED Support Center</span>
                    </div>
                  </div>
                </section>

                <SupportAiAssistantPanel
                  onTicketCreated={(created) => {
                    void loadTickets();
                    if (created?.id) {
                      setSelectedTicketId(created.id);
                    }
                    if (typeof window !== "undefined") {
                      window.dispatchEvent(new Event("treydora-support-tickets-updated"));
                    }
                  }}
                />

                {(showUnreadAlert || showNeedsUserAlert) && (
                  <div className="dsc-supportOpAlerts" role="region" aria-label="Support notifications">
                    {showUnreadAlert ? (
                      <div className="dsc-supportOpAlert">
                        <h2 className="dsc-supportOpAlert__title">Support activity</h2>
                        <p className="dsc-supportOpAlert__body">You have new support activity.</p>
                      </div>
                    ) : null}
                    {showNeedsUserAlert ? (
                      <div className="dsc-supportOpAlert dsc-supportOpAlert--needs">
                        <h2 className="dsc-supportOpAlert__title">Response needed</h2>
                        <p className="dsc-supportOpAlert__body">A support ticket is waiting for your response.</p>
                      </div>
                    ) : null}
                  </div>
                )}

                <section className="dsc-panel dg-gold-ring" aria-labelledby="dsc-ticket-title">
                  <h2 id="dsc-ticket-title" className="dsc-panel__title serif">
                    Open Support Ticket
                  </h2>
                  <p className="dsc-panel__lead">
                    Describe your issue with context and references. Submissions are stored on TreydoraEdBackend and are
                    visible only to you on this page until the TreydoraED admin inbox consumes them.
                  </p>
                  {formSuccess ? (
                    <p className="dsc-successBanner" role="status">
                      {formSuccess}
                    </p>
                  ) : null}
                  {formError ? (
                    <p className="dsc-errorBanner" role="alert">
                      {formError}
                    </p>
                  ) : null}
                  <form className="dsc-form" onSubmit={handleCreateTicket}>
                    <div className="dsc-field">
                      <label className="dsc-label" htmlFor="dsc-category">
                        Category
                      </label>
                      <select
                        id="dsc-category"
                        className="dsc-select"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        {SUPPORT_TICKET_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="dsc-field">
                      <label className="dsc-label" htmlFor="dsc-priority">
                        Priority
                      </label>
                      <select
                        id="dsc-priority"
                        className="dsc-select"
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                      >
                        {SUPPORT_PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="dsc-field dsc-field--full">
                      <label className="dsc-label" htmlFor="dsc-subject">
                        Subject <span className="dg-muted">(max 160)</span>
                      </label>
                      <input
                        id="dsc-subject"
                        className="dsc-input"
                        type="text"
                        autoComplete="off"
                        placeholder="Short summary for the ticket title"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                      />
                    </div>
                    <div className="dsc-field dsc-field--full">
                      <label className="dsc-label" htmlFor="dsc-description">
                        Description <span className="dg-muted">(10–5000 characters)</span>
                      </label>
                      <textarea
                        id="dsc-description"
                        className="dsc-textarea"
                        placeholder="What happened, what you expected, and any steps to reproduce."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </div>
                    <div className="dsc-field dsc-field--full">
                      <label className="dsc-label" htmlFor="dsc-reference">
                        Related wallet / order reference <span className="dg-muted">(optional, max 200)</span>
                      </label>
                      <input
                        id="dsc-reference"
                        className="dsc-input"
                        type="text"
                        autoComplete="off"
                        placeholder="Wallet address, Stripe receipt id, entitlement id, etc."
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                      />
                    </div>
                    <div className="dsc-field dsc-field--full">
                      <label className="dsc-label" htmlFor="dsc-create-files">
                        Attach screenshots or PDF evidence
                      </label>
                      <p className="dsc-fieldHint dg-muted">PNG, JPG, WEBP, or PDF. Max 8MB each. Up to {MAX_ATTACH} files.</p>
                      <input
                        id="dsc-create-files"
                        className="dsc-fileInput"
                        type="file"
                        accept={ACCEPT_ATTACH}
                        multiple
                        onChange={(e) => {
                          const picked = Array.from(e.target.files || []);
                          setCreateAttachmentFiles((prev) => mergeAttachmentFiles(prev, picked));
                          e.target.value = "";
                        }}
                      />
                      {createAttachmentFiles.length > 0 ? (
                        <ul className="dsc-fileChipList">
                          {createAttachmentFiles.map((f, idx) => (
                            <li key={`${f.name}-${idx}`} className="dsc-fileChip">
                              <span>{f.name}</span>
                              <button
                                type="button"
                                className="dsc-fileChip__x"
                                onClick={() =>
                                  setCreateAttachmentFiles((prev) => prev.filter((_, i) => i !== idx))
                                }
                                aria-label={`Remove ${f.name}`}
                              >
                                ×
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <div className="dsc-formActions">
                      <button type="submit" className="dg-btn dg-btn--primary" disabled={!canSubmit || formBusy}>
                        {formBusy ? "Submitting…" : "Submit ticket"}
                      </button>
                      <span className="dsc-formHint dg-muted">Requires sign-in. Tickets are private to your account.</span>
                    </div>
                  </form>
                </section>

                <section className="dsc-panel dg-gold-ring" aria-labelledby="dsc-tickets-title">
                  <div className="dsc-panelHeadRow">
                    <div>
                      <h2 id="dsc-tickets-title" className="dsc-panel__title serif">
                        My Support Requests
                      </h2>
                      <p className="dsc-panel__lead dsc-panel__lead--tight">
                        Newest activity first. Select a row to read the thread and add a follow-up.
                      </p>
                    </div>
                    <button type="button" className="dg-btn dg-btn--gold-outline dg-btn--compact" onClick={() => loadTickets()}>
                      Refresh
                    </button>
                  </div>

                  {ticketsLoading ? (
                    <p className="dsc-loadingLine dg-muted" role="status">
                      Loading support tickets…
                    </p>
                  ) : null}
                  {ticketsError ? (
                    <p className="dsc-errorBanner" role="alert">
                      {ticketsError}
                    </p>
                  ) : null}

                  {!ticketsLoading && !ticketsError && tickets.length === 0 ? (
                    <p className="dsc-emptyState">
                      No support tickets yet. Open your first request when you need help.
                    </p>
                  ) : null}

                  {!ticketsLoading && tickets.length > 0 ? (
                    <ul className="dsc-ticketList" aria-label="Your support tickets">
                      {tickets.map((t) => {
                        const active = selectedTicketId === t.id;
                        const unread = Boolean(t.unreadForUser);
                        const waitingOnUser = t.status === "Waiting on User";
                        const terminal = t.status === "Resolved" || t.status === "Closed";
                        return (
                          <li
                            key={t.id}
                            className={`dsc-ticket dsc-ticket--selectable${active ? " is-selected" : ""}${
                              unread ? " dsc-ticket--unread" : ""
                            }${waitingOnUser ? " dsc-ticket--waitingOnUser" : ""}${
                              terminal ? " dsc-ticket--terminal" : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="dsc-ticket__select"
                              onClick={() => setSelectedTicketId(t.id)}
                            >
                              {unread ? (
                                <span className="dsc-unreadDot" title="Unread update" aria-label="Unread" />
                              ) : null}
                              <span className="dsc-ticket__id">{t.ticketCode}</span>
                              <p className="dsc-ticket__subject">{t.subject}</p>
                              <p className="dsc-ticket__meta dg-muted">
                                {t.category} · {t.priority} · {formatTicketDate(t.latestActivityAt || t.createdAt)}
                                {waitingOnUser ? (
                                  <span className="dsc-ticket__waitPill"> Awaiting your reply</span>
                                ) : null}
                              </p>
                            </button>
                            <span className={badgeClassForApiStatus(t.status)}>{t.status}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}

                  {selectedTicketId ? (
                    <div className="dsc-detailPanel dg-gold-ring">
                      <h3 className="dsc-subheading">Selected ticket</h3>
                      {detailLoading ? (
                        <p className="dsc-loadingLine dg-muted">Loading conversation…</p>
                      ) : null}
                      {detailError ? <p className="dsc-errorBanner">{detailError}</p> : null}
                      {!detailLoading && detail ? (
                        <>
                          <div className="dsc-detailHeader">
                            <div>
                              {detail.unreadForUser ? (
                                <span className="dsc-unreadDot dsc-unreadDot--inline" aria-label="Unread" />
                              ) : null}
                              <span className="dsc-ticket__id">{detail.ticketCode}</span>
                              <p className="dsc-detailSubject">{detail.subject}</p>
                              <p className="dsc-ticket__meta dg-muted">
                                {detail.category} · {detail.priority} · Created{" "}
                                {formatTicketDate(detail.createdAt)} · Latest activity{" "}
                                {formatTicketDate(detail.latestActivityAt)}
                              </p>
                            </div>
                            <span className={badgeClassForApiStatus(detail.status)}>{detail.status}</span>
                          </div>
                          {detail.status === "Waiting on User" ? (
                            <div className="dsc-statusCallout dsc-statusCallout--waiting" role="status">
                              Support is waiting for your response.
                            </div>
                          ) : null}
                          {detail.status === "Resolved" ? (
                            <div className="dsc-statusCallout dsc-statusCallout--resolved" role="status">
                              This ticket has been marked resolved.
                            </div>
                          ) : null}
                          {detail.status === "Closed" ? (
                            <div className="dsc-statusCallout dsc-statusCallout--closed" role="status">
                              This ticket is closed. Open a new support request if you need more help.
                            </div>
                          ) : null}
                          {detail.relatedReference || detail.relatedWallet ? (
                            <p className="dsc-detailRef dg-muted">
                              {detail.relatedWallet ? (
                                <>
                                  <strong>Wallet:</strong> {detail.relatedWallet}{" "}
                                </>
                              ) : null}
                              {detail.relatedReference ? (
                                <>
                                  <strong>Reference:</strong> {detail.relatedReference}
                                </>
                              ) : null}
                            </p>
                          ) : null}
                          <div className="dsc-thread" aria-label="Ticket messages">
                            {(detail.messages || []).map((m) => (
                              <div key={m.id} className={`dsc-threadMsg dsc-threadMsg--${m.authorType}`}>
                                <div className="dsc-threadMsg__meta">
                                  <span className="dsc-threadMsg__who">
                                    {m.authorType === "user" ? "You" : m.authorType === "admin" ? "Treydora" : "System"}
                                  </span>
                                  <span className="dsc-threadMsg__when">{formatTicketDate(m.createdAt)}</span>
                                </div>
                                <p className="dsc-threadMsg__body">{m.body}</p>
                                <SupportMessageAttachments items={m.attachments} />
                              </div>
                            ))}
                          </div>
                          {replyLocked ? (
                            <p className="dsc-mutedNote dg-muted" role="status">
                              Follow-ups are disabled while this ticket is closed.
                            </p>
                          ) : (
                            <form className="dsc-replyForm" onSubmit={handleSendReply}>
                              <label className="dsc-label" htmlFor="dsc-reply">
                                Add follow-up
                              </label>
                              {replySuccess ? (
                                <p className="dsc-successBanner dsc-successBanner--tight" role="status">
                                  {replySuccess}
                                </p>
                              ) : null}
                              <textarea
                                id="dsc-reply"
                                className="dsc-textarea dsc-textarea--reply"
                                value={replyBody}
                                onChange={(e) => setReplyBody(e.target.value)}
                                placeholder="Additional context or answers to staff questions."
                              />
                              <div className="dsc-field dsc-field--full">
                                <label className="dsc-label" htmlFor="dsc-reply-files">
                                  Attach files (optional)
                                </label>
                                <p className="dsc-fieldHint dg-muted">PNG, JPG, WEBP, or PDF · max 8MB each · up to {MAX_ATTACH}</p>
                                <input
                                  id="dsc-reply-files"
                                  className="dsc-fileInput"
                                  type="file"
                                  accept={ACCEPT_ATTACH}
                                  multiple
                                  onChange={(e) => {
                                    const picked = Array.from(e.target.files || []);
                                    setReplyAttachmentFiles((prev) => mergeAttachmentFiles(prev, picked));
                                    e.target.value = "";
                                  }}
                                />
                                {replyAttachmentFiles.length > 0 ? (
                                  <ul className="dsc-fileChipList">
                                    {replyAttachmentFiles.map((f, idx) => (
                                      <li key={`${f.name}-r-${idx}`} className="dsc-fileChip">
                                        <span>{f.name}</span>
                                        <button
                                          type="button"
                                          className="dsc-fileChip__x"
                                          onClick={() =>
                                            setReplyAttachmentFiles((prev) => prev.filter((_, i) => i !== idx))
                                          }
                                          aria-label={`Remove ${f.name}`}
                                        >
                                          ×
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </div>
                              {replyError ? (
                                <p className="dsc-errorBanner dsc-errorBanner--tight" role="alert">
                                  {replyError}
                                </p>
                              ) : null}
                              <button
                                type="submit"
                                className="dg-btn dg-btn--gold-outline"
                                disabled={replyBusy || (replyBody.trim().length < 2 && replyAttachmentFiles.length === 0)}
                              >
                                {replyBusy ? "Sending…" : "Send follow-up"}
                              </button>
                            </form>
                          )}
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </section>

                <section className="dsc-panel dg-gold-ring" aria-labelledby="dsc-fast-title">
                  <h2 id="dsc-fast-title" className="dsc-panel__title serif">
                    Fast Help
                  </h2>
                  <p className="dsc-panel__lead">Common paths before you file — same command-room tone as the Gateway.</p>
                  <div className="dsc-fastGrid">
                    {SUPPORT_FAST_HELP.map((card) => (
                      <article key={card.key} className="dsc-fastCard">
                        <h3 className="dsc-fastCard__title">{card.title}</h3>
                        <p className="dsc-fastCard__body">{card.body}</p>
                      </article>
                    ))}
                  </div>
                </section>
              </div>

              <aside className="qd-rail questRail" aria-label="Support status and community">
                <section className="questPanel" aria-labelledby="dsc-rail-status">
                  <h2 id="dsc-rail-status" className="questPanel__title serif">
                    Support Status
                  </h2>
                  <ul className="questPanel__kv">
                    <li>
                      <span className="questPanel__label">System</span>
                      <span className="questPanel__val">Online</span>
                    </li>
                    <li>
                      <span className="questPanel__label">Ticket API</span>
                      <span className="questPanel__val">{ticketsError ? "Degraded" : "Connected"}</span>
                    </li>
                    <li>
                      <span className="questPanel__label">Route</span>
                      <span className="questPanel__val">TreydoraED Support Center</span>
                    </li>
                    <li>
                      <span className="questPanel__label">Response</span>
                      <span className="questPanel__val">Staff workflow (admin phase)</span>
                    </li>
                  </ul>
                </section>

                <section className="questPanel" aria-labelledby="dsc-rail-issues">
                  <h2 id="dsc-rail-issues" className="questPanel__title serif">
                    Known Issues
                  </h2>
                  <ul className="dsc-mutedList">
                    <li>Wallet sync: No active outage</li>
                    <li>Stripe access: Normal</li>
                    <li>Reader viewer: Normal</li>
                  </ul>
                </section>

                <section className="questPanel" aria-labelledby="dsc-rail-community">
                  <h2 id="dsc-rail-community" className="questPanel__title serif">
                    Community
                  </h2>
                  <p className="questPanel__lead dsc-communityLead">
                    Community discussion only. Account, payment, wallet, and NFT support should use official tickets.
                    Discord is secondary for lore and announcements.
                  </p>
                  <div className="dsc-communityActions">
                    {TREYDORA_DISCORD_INVITE_URL ? (
                      <a
                        className="dg-btn dg-btn--blue-outline dg-btn--compact dg-btn--wide dsc-discordBtn"
                        href={TREYDORA_DISCORD_INVITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fab fa-discord dsc-discordBtn__icon" aria-hidden />
                        Open Discord
                      </a>
                    ) : (
                      <p className="dsc-mutedNote dg-muted mb-0">
                        Set <code className="dsc-code">REACT_APP_TREYDORA_DISCORD_INVITE_URL</code> in your environment
                        to show a Discord shortcut. Official tickets remain the path for account and purchase issues.
                      </p>
                    )}
                  </div>
                </section>
              </aside>
            </div>
          </main>
        </div>
      </div>
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
